import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Drawer, Input, Select, Spin, Table, Tag } from "antd";
import type { TableColumnsType } from "antd";
import { AdminListShell } from "../components/admin/AdminListShell";
import { fetchAdminOrderDetail, fetchAdminOrdersPage } from "../api/client";
import type { AdminOrderDetail, AdminOrderRow } from "../api/types";
import { fmtDateTime, fmtUsdCents } from "../lib/formatAdmin";

const STATUS_OPTS = [
  { value: "", label: "全部状态" },
  { value: "pending", label: "pending" },
  { value: "paid", label: "paid" },
  { value: "failed", label: "failed" },
  { value: "refunded", label: "refunded" },
];

export function OrdersPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [status, setStatus] = useState("");
  const [userId, setUserId] = useState("");
  const [items, setItems] = useState<AdminOrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<"forbidden" | "error" | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detail, setDetail] = useState<AdminOrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const r = await fetchAdminOrdersPage({
      page,
      page_size: pageSize,
      status: status || undefined,
      user_id: userId || undefined,
    });
    if (r === "forbidden") setErr("forbidden");
    else if (r === "error") setErr("error");
    else {
      setItems(r.items);
      setTotal(r.total);
    }
    setLoading(false);
  }, [page, pageSize, status, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [status, userId]);

  const openDetail = (id: string) => {
    setDrawerOpen(true);
    setDetail(null);
    setDetailLoading(true);
    void (async () => {
      const r = await fetchAdminOrderDetail(id);
      setDetail(r === "forbidden" || r === "error" ? null : r);
      setDetailLoading(false);
    })();
  };

  const columns: TableColumnsType<AdminOrderRow> = [
    { title: "订单号", dataIndex: "order_no", key: "order_no", width: 200, ellipsis: true },
    {
      title: "用户ID",
      dataIndex: "user_id",
      key: "uid",
      width: 120,
      render: (id: string) => <span className="admin-ledger-mono">{id.slice(0, 8)}…</span>,
    },
    {
      title: "金额",
      key: "amt",
      width: 120,
      align: "right",
      render: (_, row) => (
        <span className="admin-ledger-num">{fmtUsdCents(row.amount_usd_cents)}</span>
      ),
    },
    { title: "币种", dataIndex: "currency", key: "cur", width: 72 },
    {
      title: "状态",
      dataIndex: "status",
      key: "st",
      width: 92,
      render: (s: string) => <Tag>{s}</Tag>,
    },
    { title: "支付时间", dataIndex: "paid_at", key: "paid", width: 160, render: fmtDateTime },
    { title: "创建时间", dataIndex: "created_at", key: "crt", width: 160, render: fmtDateTime },
    {
      title: "操作",
      key: "op",
      width: 80,
      fixed: "right",
      render: (_, row) => (
        <Button type="link" size="small" onClick={() => openDetail(row.id)}>
          详情
        </Button>
      ),
    },
  ];

  return (
    <>
      <AdminListShell
        title="订单管理"
        description="管理系统内所有交易流水与支付订单（USD，只读）。"
        err={err}
        errorBanner
        toolbar={
          <div className="admin-ledger-toolbar" style={{ flexWrap: "wrap" }}>
            <Select
              value={status || ""}
              onChange={(v) => setStatus(v)}
              options={STATUS_OPTS}
              style={{ minWidth: 120 }}
              variant="borderless"
            />
            <div className="admin-ledger-toolbar-divider" />
            <Input
              placeholder="用户 UUID 过滤"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              variant="borderless"
              style={{ width: 220 }}
              allowClear
            />
          </div>
        }
      table={(bodyY) => (
        <div className="admin-ledger-table-wrap admin-ledger-table-wrap--flex">
          <Spin spinning={loading}>
            <Table<AdminOrderRow>
              rowKey="id"
              className="admin-ledger-table"
              columns={columns}
              dataSource={items}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50],
                showTotal: (t) => `共 ${t} 条`,
                onChange: (p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                },
              }}
              scroll={{ x: 1200, y: bodyY }}
              locale={{ emptyText: "暂无订单" }}
            />
          </Spin>
        </div>
      )}
    />

      <Drawer
        title={null}
        placement="right"
        size="40%"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        destroyOnHidden
        className="admin-drawer"
      >
        <div className="admin-drawer-kicker">ORDER DETAIL</div>
        {detailLoading ? (
          <div style={{ padding: 48, textAlign: "center" }}>
            <Spin />
          </div>
        ) : detail ? (
          <>
            <div className="admin-drawer-title-row">
              <Tag color={detail.status === "paid" ? "green" : undefined}>{detail.status}</Tag>
              <h2 className="admin-drawer-title">{detail.order_no}</h2>
            </div>
            <section className="admin-drawer-section">
              <h3 className="admin-drawer-section-title">支付信息</h3>
              <div className="admin-drawer-kv">
                <div className="admin-drawer-kv-item">
                  <label>金额</label>
                  <div className="val val--accent">{fmtUsdCents(detail.amount_usd_cents)}</div>
                </div>
                <div className="admin-drawer-kv-item">
                  <label>币种</label>
                  <div className="val">{detail.currency}</div>
                </div>
                <div className="admin-drawer-kv-item">
                  <label>支付时间</label>
                  <div className="val">{fmtDateTime(detail.paid_at)}</div>
                </div>
                <div className="admin-drawer-kv-item">
                  <label>更新时间</label>
                  <div className="val">{fmtDateTime(detail.updated_at)}</div>
                </div>
              </div>
            </section>
            <section className="admin-drawer-section">
              <h3 className="admin-drawer-section-title">用户与套餐</h3>
              <div className="admin-drawer-card">
                <div className="admin-drawer-kv">
                  <div className="admin-drawer-kv-item">
                    <label>用户邮箱</label>
                    <div className="val">{detail.user_email ?? "—"}</div>
                  </div>
                  <div className="admin-drawer-kv-item">
                    <label>套餐</label>
                    <div className="val">
                      <strong>{detail.plan_name ?? "—"}</strong>
                      {detail.plan_code ? (
                        <span className="admin-ledger-mono" style={{ marginLeft: 8 }}>
                          {detail.plan_code}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <div className="admin-drawer-footer">
              <button
                type="button"
                className="admin-btn-primary-ledger"
                onClick={() => setDrawerOpen(false)}
              >
                关闭
              </button>
            </div>
          </>
        ) : (
          <Alert type="warning" showIcon message="未加载到订单" />
        )}
      </Drawer>
    </>
  );
}
